import React, { useState, useEffect } from "react";
import Modal from "../../../components/modal";
import { API_URL } from "../../../utils/constant";
import axios from "axios";
import { Check, Layers, CheckCircle2 } from "lucide-react";
import Swal from "sweetalert2";

const UpdatePurchaseStockPage = ({ show, onClose, heading, initialData = {}, onSubmit }) => {
    const [finalProduct, setFinalProduct] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [allocateQty, setAllocateQty] = useState('');
    const [allocationType, setAllocationType] = useState('Retail');
    const [retailPrice, setRetailPrice] = useState('');
    const [wholesalePrice, setWholesalePrice] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const purchasesList = initialData?.PurchasesProducts || [];
    const unallocatedList = purchasesList.filter(item => (item.unallocated_qty || 0) > 0);

    useEffect(() => {
        if (show && initialData?.id) {
            axios.get(`${API_URL}/final-products/by-raw-product/${initialData.id}`)
                .then(res => {
                    if (res.data && res.data.success) {
                        const fp = res.data.data;
                        setFinalProduct(fp);
                    }
                })
                .catch(err => {
                    console.error("Error fetching final product details:", err);
                });

            // Default select first unallocated item
            const firstUnallocated = unallocatedList[0] || purchasesList[0] || null;
            if (firstUnallocated) {
                selectItem(firstUnallocated);
            } else {
                setSelectedItemId(null);
                setAllocateQty('');
                setRetailPrice('');
                setWholesalePrice('');
            }
        } else {
            setFinalProduct(null);
            setSelectedItemId(null);
            setAllocateQty('');
            setRetailPrice('');
            setWholesalePrice('');
            setIsSubmitting(false);
        }
    }, [show, initialData]);

    const selectItem = (item) => {
        if (!item) return;
        setSelectedItemId(item.id);
        const fresh = item.unallocated_qty || 0;
        setAllocateQty(fresh > 0 ? fresh : '');

        // Default prices from PurchasesProduct or FinalProduct
        const defaultRetail = item.per_unit_actual_price || item.price || finalProduct?.final_selling_price || initialData?.final_selling_price || '';
        const defaultWholesale = finalProduct?.wholesale_price || defaultRetail || '';

        setRetailPrice(defaultRetail ? parseFloat(defaultRetail).toFixed(2) : '');
        setWholesalePrice(defaultWholesale ? parseFloat(defaultWholesale).toFixed(2) : '');
    };

    const selectedItem = purchasesList.find(i => i.id === selectedItemId) || unallocatedList[0] || null;

    const handleAllocate = (e) => {
        e.preventDefault();
        const qtyNum = parseInt(allocateQty, 10);
        if (!qtyNum || qtyNum <= 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Quantity',
                text: 'Please enter a valid allocation quantity greater than 0.'
            });
            return;
        }

        const maxAvailable = selectedItem ? selectedItem.unallocated_qty : 0;
        if (maxAvailable > 0 && qtyNum > maxAvailable) {
            Swal.fire({
                icon: 'warning',
                title: 'Quantity Exceeds Stock',
                text: `You cannot allocate more than the fresh stock available (${maxAvailable}).`
            });
            return;
        }

        const company = JSON.parse(localStorage.getItem('company'));
        const supplierId = selectedItem?.Purchase?.vendor_id || initialData.RawProductVendors?.[0]?.vendor_id;
        const isDispensing = initialData?.isDispensingMaterial || false;

        const requestData = {
            supplier_id: supplierId,
            raw_product_id: initialData?.id,
            allocated_quantity: qtyNum,
            allocation_type: isDispensing ? 'Dispensing' : allocationType,
            price: retailPrice ? parseFloat(retailPrice) : 0,
            wholesale_price: wholesalePrice ? parseFloat(wholesalePrice) : 0,
            is_dispensing: isDispensing,
            ...(selectedItem ? { purchases_product_id: selectedItem.id } : {})
        };

        setIsSubmitting(true);

        fetch(`${API_URL}/raw-products/update-stock/${initialData.id}/${company.company?.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        })
            .then(response => response.json())
            .then(data => {
                setIsSubmitting(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Stock Allocated!',
                    text: isDispensing
                        ? `${qtyNum} units allocated successfully to Dispensing Materials inventory.`
                        : `${qtyNum} units allocated successfully to ${allocationType} store.`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                if (onSubmit) onSubmit();
                onClose();
            })
            .catch(error => {
                setIsSubmitting(false);
                console.error("Error updating stock:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Allocation Failed',
                    text: error.message || 'Something went wrong while allocating stock.'
                });
            });
    };

    const totalFresh = unallocatedList.reduce((sum, i) => sum + (i.unallocated_qty || 0), 0);

    return (
        <Modal
            show={show}
            onClose={onClose}
            heading={heading || "Update Quantity & Allocate Stock"}
        >
            <div style={{ maxHeight: '80vh', overflowY: 'auto' }} className="px-1">
                {/* Product Banner */}
                <div className="card shadow-sm border-0 bg-light rounded-3 p-3 mb-3">
                    <div className="row align-items-center g-3">
                        <div className="col-auto">
                            <img
                                src={`${API_URL}/${initialData?.product_image}`}
                                width="65px"
                                height="65px"
                                className="rounded-3 shadow-sm object-fit-cover border bg-white"
                                onError={(e) => { e.target.src = 'https://placehold.co/65x65?text=P'; }}
                                alt={initialData?.name}
                            />
                        </div>
                        <div className="col">
                            <div className="text-secondary fw-semibold" style={{ fontSize: '13px' }}>Product Name</div>
                            <h4 className="mb-1 text-dark fw-bold">{initialData?.name}</h4>
                            <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                                <span className="badge bg-dark text-white px-2 py-1" style={{ fontSize: '13px' }}>
                                    SKU: {initialData?.sku || 'N/A'}
                                </span>
                                <span className="badge bg-secondary text-white px-2 py-1" style={{ fontSize: '13px' }}>
                                    HSN: {initialData?.hsn_code || 'N/A'}
                                </span>
                            </div>
                        </div>
                        <div className="col-auto text-end">
                            <div className="text-secondary fw-semibold" style={{ fontSize: '13px' }}>Total Fresh Stock</div>
                            <span className="badge bg-primary px-3 py-2 fw-bold">
                                {totalFresh} Units
                            </span>
                        </div>
                    </div>
                </div>

                {/* Potency & Volume Selection Table */}
                <div className="card shadow-sm border border-secondary-subtle rounded-3 mb-3">
                    <div className="card-header bg-white py-2 px-3 d-flex align-items-center justify-content-between">
                        <span className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '15px' }}>
                            <Layers size={18} className="text-primary" />
                            Select Potency & Volume to Allocate:
                        </span>
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1" style={{ fontSize: '13px' }}>
                            {unallocatedList.length} Variant Line{unallocatedList.length > 1 ? 's' : ''} Available
                        </span>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0" style={{ fontSize: '14px' }}>
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '50px' }} className="text-center">Select</th>
                                    <th>Potency</th>
                                    <th>Volume</th>
                                    <th>Batch No</th>
                                    <th className="text-center">Purchased</th>
                                    <th className="text-center text-primary fw-bold">Fresh Stock</th>
                                    <th>MRP</th>
                                    <th>Invoice #</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unallocatedList.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted py-4 fs-6">
                                            No fresh unallocated stock available for this product.
                                        </td>
                                    </tr>
                                ) : (
                                    unallocatedList.map((item) => {
                                        const isSelected = selectedItemId === item.id;
                                        return (
                                            <tr
                                                key={item.id}
                                                onClick={() => selectItem(item)}
                                                className={`cursor-pointer ${isSelected ? 'table-primary border-primary' : ''}`}
                                                style={{ cursor: 'pointer', height: '48px' }}
                                            >
                                                <td className="text-center">
                                                    <input
                                                        type="radio"
                                                        name="selectedPurchaseItem"
                                                        checked={isSelected}
                                                        onChange={() => selectItem(item)}
                                                        className="form-check-input cursor-pointer"
                                                        style={{ width: '18px', height: '18px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <span className={`badge ${isSelected ? 'bg-primary text-white' : 'bg-info text-dark border'} px-2 py-1 fw-bold`} style={{ fontSize: '13px' }}>
                                                        {item.potency || 'Standard'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge ${isSelected ? 'bg-primary text-white' : 'bg-light text-dark border border-secondary'} px-2 py-1 fw-bold`} style={{ fontSize: '13px' }}>
                                                        {item.volume || 'Standard'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="font-monospace fw-bold text-dark" style={{ fontSize: '14px' }}>
                                                        {item.batch_no || '-'}
                                                    </span>
                                                </td>
                                                <td className="text-center text-muted fw-semibold">{item.quantity}</td>
                                                <td className="text-center fw-bold text-primary">
                                                    {item.unallocated_qty}
                                                </td>
                                                <td className="fw-bold text-dark">
                                                    ₹{parseFloat(item.per_unit_actual_price || item.price || 0).toFixed(2)}
                                                </td>
                                                <td className="text-secondary fw-semibold">
                                                    {item.Purchase?.bill_number || `Bill #${item.purchases_id || '-'}`}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Allocation Form */}
                <form onSubmit={handleAllocate} className="card shadow-sm border border-primary rounded-3 p-3 bg-white mb-2">
                    <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                        <span className="fw-bold text-primary">
                            Allocation Details
                        </span>
                        {selectedItem && (
                            <span className="badge bg-primary text-white px-3 py-2 shadow-sm">
                                <CheckCircle2 size={16} className="me-1" />
                                Active: <strong>{[selectedItem.potency, selectedItem.volume].filter(Boolean).join(' ') || 'Standard'}</strong>
                                {selectedItem.batch_no ? ` | Batch: ${selectedItem.batch_no}` : ''}
                            </span>
                        )}
                    </div>

                    <div className="row g-3">
                        {/* Destination */}
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-dark mb-1" style={{ fontSize: '14px' }}>
                                Allocation Destination <span className="text-danger">*</span>
                            </label>
                            {initialData?.isDispensingMaterial ? (
                                <input
                                    type="text"
                                    readOnly
                                    value="Dispensing Materials Inventory"
                                    className="form-control form-control-lg bg-light text-info fw-bold"
                                    style={{ fontSize: '15px' }}
                                />
                            ) : (
                                <select
                                    className="form-select form-select-lg fw-bold"
                                    value={allocationType}
                                    onChange={(e) => setAllocationType(e.target.value)}
                                    style={{ fontSize: '15px' }}
                                >
                                    <option value="Retail">Retail Store (qty)</option>
                                    <option value="Wholesale">Wholesale Store (current_wholesale)</option>
                                </select>
                            )}
                        </div>

                        {/* Allocate Quantity */}
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-dark mb-1" style={{ fontSize: '14px' }}>
                                Allocate Quantity <span className="text-danger">*</span>
                                {selectedItem && (
                                    <span className="text-primary ms-1 fw-bold">
                                        (Max: {selectedItem.unallocated_qty})
                                    </span>
                                )}
                            </label>
                            <input
                                type="number"
                                required
                                min="1"
                                max={selectedItem ? selectedItem.unallocated_qty : undefined}
                                value={allocateQty}
                                onChange={(e) => setAllocateQty(e.target.value)}
                                placeholder="Enter Quantity"
                                className="form-control form-control-lg fw-bold text-primary"
                                style={{ fontSize: '16px' }}
                            />
                        </div>

                        {/* Supplier Info */}
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-dark mb-1" style={{ fontSize: '14px' }}>
                                Supplier Name
                            </label>
                            <input
                                type="text"
                                readOnly
                                value={selectedItem?.Purchase?.vendor_id ? (initialData.RawProductVendors?.find(v => v.vendor_id === selectedItem.Purchase.vendor_id)?.suppliers?.name || 'WSI') : 'Supplier'}
                                className="form-control form-control-lg bg-light text-dark fw-semibold"
                                style={{ fontSize: '15px' }}
                            />
                        </div>

                        {/* Retail Price */}
                        <div className="col-md-6">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="form-label fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>
                                    Retail Selling Price (₹)
                                </label>
                                {selectedItem && (selectedItem.per_unit_actual_price || selectedItem.price) && (
                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace" style={{ fontSize: '12px' }}>
                                        MRP: ₹{parseFloat(selectedItem.per_unit_actual_price || selectedItem.price || 0).toFixed(2)}
                                    </span>
                                )}
                            </div>
                            <div className="input-group input-group-lg">
                                <span className="input-group-text bg-light fw-bold">₹</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={retailPrice}
                                    onChange={(e) => setRetailPrice(e.target.value)}
                                    placeholder="Retail Price"
                                    className="form-control fw-bold"
                                    style={{ fontSize: '16px' }}
                                />
                            </div>
                            <span className="text-muted fw-semibold" style={{ fontSize: '12px' }}>
                                Pre-filled from MRP ({selectedItem ? `₹${parseFloat(selectedItem.per_unit_actual_price || selectedItem.price || 0).toFixed(2)}` : 'MRP'}), editable.
                            </span>
                        </div>

                        {/* Wholesale Price */}
                        <div className="col-md-6">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="form-label fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>
                                    Wholesale Price (₹)
                                </label>
                                {selectedItem && (selectedItem.per_unit_actual_price || selectedItem.price) && (
                                    <span className="badge bg-success-subtle text-success border border-success-subtle font-monospace" style={{ fontSize: '12px' }}>
                                        MRP: ₹{parseFloat(selectedItem.per_unit_actual_price || selectedItem.price || 0).toFixed(2)}
                                    </span>
                                )}
                            </div>
                            <div className="input-group input-group-lg">
                                <span className="input-group-text bg-light fw-bold">₹</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={wholesalePrice}
                                    onChange={(e) => setWholesalePrice(e.target.value)}
                                    placeholder="Wholesale Price"
                                    className="form-control fw-bold"
                                    style={{ fontSize: '16px' }}
                                />
                            </div>
                            <span className="text-muted fw-semibold" style={{ fontSize: '12px' }}>
                                Pre-filled from MRP ({selectedItem ? `₹${parseFloat(selectedItem.per_unit_actual_price || selectedItem.price || 0).toFixed(2)}` : 'MRP'}), editable.
                            </span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4 py-2 fw-bold"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary px-5 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
                            disabled={isSubmitting || !selectedItem || (selectedItem.unallocated_qty || 0) <= 0}
                        >
                            <Check size={20} />
                            {isSubmitting ? 'Allocating...' : `Allocate Stock to ${allocationType}`}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default UpdatePurchaseStockPage;
